<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import {
  getEventStatusBadge,
  formatFollowUpTime,
} from 'dashboard/components-next/FollowUp/composables/useFollowUp.js';

const props = defineProps({
  events: { type: Array, required: true },
});

// Reschedule/Complete need a dialog and a refetch that only the Calendar
// page can own — this view just reports what the agent picked.
const emit = defineEmits(['reschedule', 'complete']);

const { t, locale } = useI18n();
const router = useRouter();

// Each event's badge is computed once here (not per template evaluation),
// and via the same meeting-aware helper the Month/Week/Day event chips use,
// so an event never shows a different status color depending on which view
// mode is active.
const groupedByDay = computed(() => {
  const sorted = [...props.events]
    .sort((a, b) => a.date - b.date)
    .map(event => ({
      ...event,
      badge: getEventStatusBadge(event.date, event.isMeeting),
    }));
  const groups = [];
  sorted.forEach(event => {
    const key = event.date.toDateString();
    let group = groups.find(g => g.key === key);
    if (!group) {
      group = { key, date: event.date, events: [] };
      groups.push(group);
    }
    group.events.push(event);
  });
  return groups;
});

const dayLabel = date =>
  date.toLocaleDateString(locale.value, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });

const openContact = contactId => {
  router.push({ name: 'contacts_edit', params: { contactId } });
};

const openMenuForContactId = ref(null);
const menuItems = computed(() => [
  {
    label: t('CALENDAR.EVENT_MENU.JOIN'),
    action: 'join',
    value: 'join',
    icon: 'i-lucide-video',
  },
  {
    label: t('CALENDAR.EVENT_MENU.RESCHEDULE'),
    action: 'reschedule',
    value: 'reschedule',
    icon: 'i-lucide-calendar-clock',
  },
  {
    label: t('CALENDAR.EVENT_MENU.OPEN_CONTACT'),
    action: 'open_contact',
    value: 'open_contact',
    icon: 'i-lucide-user',
  },
  {
    label: t('CALENDAR.EVENT_MENU.COMPLETE'),
    action: 'complete',
    value: 'complete',
    icon: 'i-lucide-check',
  },
]);

const handleMenuAction = (event, { action }) => {
  openMenuForContactId.value = null;
  if (action === 'join') {
    window.open(
      event.contact.custom_attributes?.meeting_url,
      '_blank',
      'noopener'
    );
  } else if (action === 'reschedule') emit('reschedule', event);
  else if (action === 'open_contact') openContact(event.contact.id);
  else if (action === 'complete') emit('complete', event);
};
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 gap-4 overflow-y-auto">
    <div v-if="!events.length" class="py-10 text-sm text-center text-n-slate-9">
      {{ t('CALENDAR.NO_EVENTS') }}
    </div>
    <div
      v-for="group in groupedByDay"
      :key="group.key"
      class="flex flex-col gap-2"
    >
      <h3
        class="px-1 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
      >
        {{ dayLabel(group.date) }}
      </h3>
      <div
        class="flex flex-col overflow-hidden border divide-y rounded-lg border-n-weak divide-n-weak"
      >
        <div
          v-for="event in group.events"
          :key="event.contact.id"
          role="button"
          tabindex="0"
          class="relative flex items-center gap-3 px-3 py-2.5 text-left cursor-pointer hover:bg-n-alpha-1"
          @click="openContact(event.contact.id)"
          @keydown.enter="openContact(event.contact.id)"
        >
          <span
            class="text-xs font-semibold text-n-blue-11 tabular-nums w-16 flex-shrink-0"
          >
            {{ formatFollowUpTime(event.date) }}
          </span>
          <Avatar
            :name="event.contact.name"
            :src="event.contact.thumbnail"
            :size="24"
          />
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ event.contact.name }}
          </span>
          <button
            v-if="event.isMeeting"
            v-on-clickaway="() => (openMenuForContactId = null)"
            type="button"
            class="flex-shrink-0"
            @click.stop="
              openMenuForContactId =
                openMenuForContactId === event.contact.id
                  ? null
                  : event.contact.id
            "
          >
            <Icon
              icon="i-lucide-video"
              class="flex-shrink-0 size-3.5 text-n-iris-10"
            />
          </button>
          <span
            v-if="event.contact.assignee"
            class="ml-auto flex items-center gap-1.5 text-xs text-n-slate-10 flex-shrink-0"
          >
            <Avatar
              :name="event.contact.assignee.name"
              :src="event.contact.assignee.thumbnail"
              :size="16"
            />
            {{ event.contact.assignee.name }}
          </span>
          <Label
            v-if="event.badge"
            :label="t(event.badge.labelKey)"
            :color="event.badge.color"
            compact
            :class="event.contact.assignee ? '' : 'ml-auto'"
          />
          <DropdownMenu
            v-if="openMenuForContactId === event.contact.id"
            :menu-items="menuItems"
            class="z-10 mt-1 w-40 ltr:right-3 rtl:left-3 top-full"
            @action="action => handleMenuAction(event, action)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
